import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { TOAST } from "@/shared/constants/toastMessages";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { supabase } from "@/shared/lib/supabase";
import { sentryUtils } from "@/shared/utils/sentry";
import { toRoleFlag } from "@/shared/utils/syncRoleColumns";

import type { PersonContact } from "@/shared/types/estateContact";

/**
 * Gives `role` to exactly the contacts in `contactIds`. `hasRole` recognizes every spelling
 * of the role already stored, so those are removed from anyone who no longer holds it.
 */
export function useAssignLegalRole() {
  const { user, person } = useAppContext();
  const queryClient = useQueryClient();
  const { contacts, updateContactAsync } = useUserContacts();

  const saveUserRoleFlags = async (flags: Record<string, boolean>) => {
    if (!user?.user_id || !person?.estate_id || Object.keys(flags).length === 0) return;
    const { error } = await supabase
      .from("user_estates")
      .update(flags)
      .eq("user_id", user.user_id)
      .eq("estate_id", person.estate_id);
    if (error) throw error;
    queryClient.invalidateQueries({ queryKey: ["userEstateRole"] });
  };

  return async (
    role: string,
    hasRole: (contact: PersonContact) => boolean,
    contactIds: number[],
  ) => {
    // The cache, not this render's list, so back-to-back calls see each other's optimistic updates
    const latest =
      queryClient.getQueriesData<PersonContact[]>({
        queryKey: ["person-contacts", person?.estate_id],
      })[0]?.[1] ?? contacts;
    try {
      await Promise.all(
        latest
          .filter((contact) => hasRole(contact) !== contactIds.includes(contact.id))
          .map(async (contact) => {
            const roles: string[] = contact.legal_role ?? [];
            const gaining = contactIds.includes(contact.id);
            const changedRoles = gaining
              ? [role]
              : roles.filter((r) => hasRole({ ...contact, legal_role: [r] }));
            // useUserContacts rewrites the user's own flagged roles from user_estates, so change them there first
            if (user?.email && contact.email === user.email) {
              await saveUserRoleFlags(
                Object.fromEntries(
                  changedRoles.flatMap((r) => {
                    const flag = toRoleFlag(r);
                    return flag ? [[flag, gaining] as const] : [];
                  }),
                ),
              );
            }
            const legal_role = gaining
              ? [...roles, role]
              : roles.filter((r) => !changedRoles.includes(r));
            return updateContactAsync({ id: contact.id, legal_role });
          }),
      );
    } catch (err) {
      enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
      sentryUtils.captureError(err, "Assigning a legal role failed", { role });
    }
  };
}
